import React, { useState, useEffect } from 'react'

interface Location {
  id: number
  location: string
  country_code: string
  country_name: string
  phone_prefix: string
  is_active: boolean
}

interface MobileInputProps {
  selectedLocation: string
  mobileNumber: string
  onMobileChange: (value: string) => void
  className?: string
  required?: boolean
  id?: string
  disabled?: boolean
}

export const MobileInput: React.FC<MobileInputProps> = ({
  selectedLocation,
  mobileNumber,
  onMobileChange,
  className = '',
  required = false,
  id,
  disabled = false
}) => {
  const [locations, setLocations] = useState<Location[]>([])
  const [countryCode, setCountryCode] = useState<string>('')
  const [loading, setLoading] = useState(true)

  // Fetch locations from backend
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        // Use direct backend URL to avoid proxy issues
        const response = await fetch('http://localhost:3000/api/locations')
        if (response.ok) {
          const data = await response.json()
          setLocations(data.locations || [])
        } else {
          console.error('Failed to fetch locations:', response.status)
        }
      } catch (error) {
        console.error('Failed to fetch locations:', error)
        // Fallback data for development
        setLocations([
          { id: 1, location: 'DUBAI', country_code: 'AE', country_name: 'United Arab Emirates', phone_prefix: '+971', is_active: true },
          { id: 2, location: 'DOHA', country_code: 'QA', country_name: 'Qatar', phone_prefix: '+974', is_active: true }
        ])
      } finally {
        setLoading(false)
      }
    }

    fetchLocations()
  }, [])

  // Update country code when location changes
  useEffect(() => {
    if (selectedLocation && locations.length > 0) {
      const location = locations.find(loc => 
        loc.location.toUpperCase() === selectedLocation.toUpperCase()
      )
      if (location) {
        setCountryCode(location.phone_prefix)
      } else {
        setCountryCode('')
      }
    } else {
      setCountryCode('')
    }
  }, [selectedLocation, locations])

  // Filter input to only allow digits and limit to 9 characters
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 9)
    onMobileChange(value)
  }

  if (loading) {
    return (
      <div className="mobile-input-container">
        <div className="mobile-input-wrapper">
          <div className="country-code-display">
            <div className="animate-pulse bg-gray-300 h-4 w-8 rounded"></div>
          </div>
          <div className="flex-1 p-3">
            <div className="animate-pulse bg-gray-300 h-4 w-24 rounded"></div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="mobile-input-container">
      <div className="mobile-input-wrapper">
        {/* Country Code Display */}
        <div className="country-code-display">
          {countryCode || '+---'}
        </div>
        
        {/* Mobile Number Input */}
        <input
          type="text"
          id={id}
          value={mobileNumber}
          onChange={handleInputChange}
          placeholder="Mobile Number *"
          maxLength={9}
          className={`mobile-number-input ${className}`}
          required={required}
          inputMode="numeric"
          pattern="[0-9]*"
          disabled={disabled}
        />
        {/* Inline Character Counter */}
        {mobileNumber.length > 0 && (
          <div className="inline-char-counter">
            {mobileNumber.length}/9
          </div>
        )}
      </div>
    </div>
  )
} 